import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { status } from 'constants/general';
import SearchBar from 'modules/common/components/SearchBar/SearchBar';
import AddBranchProvidersModal from 'modules/modals/AddBranchProvidersModal';
import categoriesStrings from 'modules/categories/strings';
import { branchBookingTypes } from 'constants/bookingTypes';
import { providerTypes } from 'constants/providerTypes';
import initialIcon from 'assets/images/empty-providers.svg';

import Table from 'modules/common/components/Table';
import { TableItemType } from 'modules/common/components/Table/enums';
import DropdownFilter from 'modules/common/components/DropdownFilter';
import Toggle from 'modules/common/components/Toggle';
import appModeService from 'services/AppModeService';
import themeData from 'utils/theme/themeData';
import {
  Container,
  Center,
  Img,
  Text,
  Button,
  Wrapper,
  OrderNumberOverlay,
} from './ClinicServicesProvidersComponent.style';

const ClinicServicesProvidersComponent = ({
  isCreateProviderModalHidden,
  setIsCreateProviderModalHidden,
  filtersSelected,
  searchQuery,
  handleSearchBarInputChange,
  handleShowMoreClick,
  handleRemoveProvider,
  handleProviderDeactivate,
  handleExclusiveToggle,
  priceListId,
  handleDropDownFilterChange,
}) => {
  const [providersRecords, setProvidersRecords] = useState([]);
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);
  const providers = useSelector(state => state.categories.providers);
  const dropdownsListStatus = useSelector(state => state.categories.dropdownsList.status);
  const areas = useSelector(state => state.categories.dropdownsList.areasList);

  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 2000);
  };

  const getMainRowItems = () => {
    const rows = [];
    providers.data?.forEach(item => {
      const { id, provider, active, exclusive } = item;
      const providerArea = areas?.find(area => area.key === provider?.areaKey);
      const providerName = `${provider?.providerName}${
        providerArea?.name ? ` - ${providerArea?.name}` : ''
      }`;

      const rowContent = [
        {
          itemContent: providerName,
          itemContentURL: provider?.providerPhoto,
          itemType: TableItemType.TextWithPhoto,
          itemPath: `${item.id}`,
          isItemClickable: false,
        },
        {
          itemContent: provider?.providerKey,
          itemType: TableItemType.Text,
          isItemClickable: true,
          itemClickAction: () => {
            handleTextToCopyToClipboardClick(provider?.providerKey);
          },
        },
        {
          itemContent: active,
          itemType: TableItemType.Status,
          isItemClickable: false,
        },
      ];

      // Add exclusive toggle only in standalone mode
      if (!appModeService.isEmbedded) {
        rowContent.push({
          itemContent: (
            <Toggle isActive={exclusive} onClick={() => handleExclusiveToggle(item)} isEnabled />
          ),
          itemType: TableItemType.Component,
          isItemClickable: false,
        });
      }

      rows.push({
        id,
        rowData: item,
        rowContent,
        editButtons: [
          {
            btnId: 2,
            btnIcon: active ? '"\\e91f"' : '"\\e908"',
            btnLabel: active ? 'Deactivate' : 'Activate',
            btnFn: () => handleProviderDeactivate(item),
          },
          {
            btnId: 1,
            btnIcon: '"\\e91d"',
            btnLabel: 'Remove',
            btnFn: () => handleRemoveProvider(item),
            btnTextColor: themeData.colors.redMain,
          },
        ],
      });
    });

    return rows;
  };

  useEffect(() => {
    setProvidersRecords(() => getMainRowItems());
  }, [providers.data, areas]);

  // Get table columns based on embedded mode
  const getTableColumns = () => {
    const { gridHeaderList } = categoriesStrings.clinicServicesProvidersTable;
    if (appModeService.isEmbedded) {
      const { component, ...baseColumns } = gridHeaderList;
      return baseColumns;
    }
    return gridHeaderList;
  };

  // Get grid style based on embedded mode
  const getGridStyle = () =>
    appModeService.isEmbedded
      ? 'grid-template-columns: 1fr 1fr 1fr 60px;'
      : 'grid-template-columns: 1fr 1fr 1fr 140px 60px;';

  return (
    <Container>
      {providers.status === status.SUCCESS &&
      providers.data.length === 0 &&
      !searchQuery &&
      Object.keys(filtersSelected).length === 0 ? (
        <Center>
          <Img src={initialIcon} />
          <Text>{categoriesStrings.clinicServicesProviders.emptyState}</Text>
          <Button onClick={() => setIsCreateProviderModalHidden(false)}>
            {categoriesStrings.clinicServicesProviders.addProvider}
          </Button>
        </Center>
      ) : (
        <Wrapper>
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={() => setIsCreateProviderModalHidden(false)}
          >
            {categoriesStrings.clinicServicesProviders.addProvider}
          </Button>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchBarInputChange={handleSearchBarInputChange}
            testId="healthplansearch"
          >
            <DropdownFilter
              dropdownMenuItemsList={areas}
              dropdownMenuPlaceholder={categoriesStrings.filters.areaFilterPlaceholder}
              showLoader={dropdownsListStatus === status.FETCHING}
              handleDropdownMenuChange={value => handleDropDownFilterChange('AreaKey', value)}
              emptyStateText={categoriesStrings.filters.emptyAreaFilter}
            />
          </SearchBar>
          <Table
            latestDataLength={providers.latestDataLength}
            dataStatus={providers.status}
            columns={getTableColumns()}
            rows={providersRecords}
            filtersSelected={filtersSelected}
            searchQuery={searchQuery}
            handleShowMoreClick={handleShowMoreClick}
            extendTableStyle={getGridStyle()}
            showTableDivider={false}
          />
        </Wrapper>
      )}

      {!isCreateProviderModalHidden && (
        <AddBranchProvidersModal
          onClose={() => setIsCreateProviderModalHidden(true)}
          priceListId={priceListId}
          bookingTypeId={branchBookingTypes.PHYSCIAL}
          modalStrings={categoriesStrings.clinicServicesModal}
          providerTypeId={providerTypes.INCLINIC_SERVICES}
        />
      )}
      {/** Widget */}
      {showCopiedTextOverlay && <OrderNumberOverlay>Copied to clipboard 🚀</OrderNumberOverlay>}
    </Container>
  );
};

ClinicServicesProvidersComponent.propTypes = {
  isCreateProviderModalHidden: PropTypes.bool.isRequired,
  setIsCreateProviderModalHidden: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handleSearchBarInputChange: PropTypes.func.isRequired,
  providersQueryString: PropTypes.shape({
    healthPlanId: PropTypes.number,
    categoryId: PropTypes.number,
    pageNumber: PropTypes.number,
    pagesize: PropTypes.number,
    active: PropTypes.bool,
  }).isRequired,
  handleRemoveProvider: PropTypes.func.isRequired,
  handleProviderDeactivate: PropTypes.func.isRequired,
  handleExclusiveToggle: PropTypes.func.isRequired,
  handleDropDownFilterChange: PropTypes.func.isRequired,
};

export default ClinicServicesProvidersComponent;
